<template>
  <div class="collective-page">
    <div class="page-header">
      <div>
        <h2>快递集送配置</h2>
        <p>按学校维护价格、取件地点、每日时段及集送人员</p>
      </div>
      <el-select v-model="schoolId" filterable placeholder="请选择学校" :disabled="schoolLocked" @change="loadConfig">
        <el-option v-for="school in schools" :key="school.schoolId" :label="school.schoolName" :value="school.schoolId" />
      </el-select>
    </div>

    <div class="section-block" v-loading="loading">
      <div class="section-heading">
        <div>
          <h3>学校配置</h3>
          <span>集送时段默认提前 10 分钟停止下单</span>
        </div>
        <el-button type="primary" icon="el-icon-check" @click="saveConfig">保存配置</el-button>
      </div>
      <el-form :model="config" label-width="110px" class="config-form">
        <el-form-item label="是否启用">
          <el-switch v-model="config.enabled" :active-value="1" :inactive-value="0" active-text="启用" inactive-text="关闭" />
        </el-form-item>
        <el-form-item label="小件价格">
          <el-input-number v-model="config.smallPrice" :min="0" :precision="2" :step="0.5" />
          <span class="unit-text">元</span>
        </el-form-item>
        <el-form-item label="中件价格">
          <el-input-number v-model="config.mediumPrice" :min="0" :precision="2" :step="0.5" />
          <span class="unit-text">元</span>
        </el-form-item>
        <el-form-item label="大件价格">
          <el-input-number v-model="config.largePrice" :min="0" :precision="2" :step="0.5" />
          <span class="unit-text">元</span>
        </el-form-item>
      </el-form>
    </div>

    <div class="section-block">
      <div class="section-heading">
        <div>
          <h3>取件地点</h3>
          <span>用户下单时只能从启用的地点中选择</span>
        </div>
        <el-button type="primary" icon="el-icon-plus" @click="openLocation()">新增地点</el-button>
      </div>
      <el-table :data="locations" border empty-text="暂无取件地点">
        <el-table-column prop="locationName" label="地点名称" min-width="240" />
        <el-table-column prop="sort" label="排序" width="100" align="center" />
        <el-table-column label="状态" width="100" align="center">
          <template slot-scope="scope">
            <el-tag :type="scope.row.status === 1 ? 'success' : 'info'">{{ scope.row.status === 1 ? '启用' : '禁用' }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="150" align="center">
          <template slot-scope="scope">
            <el-button type="text" icon="el-icon-edit" @click="openLocation(scope.row)">编辑</el-button>
            <el-button type="text" class="danger-text" icon="el-icon-delete" @click="removeLocation(scope.row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <div class="section-block">
      <div class="section-heading">
        <div>
          <h3>每日集送时段</h3>
          <span>每个时间绑定一名本校负责人或兼职，每天重复执行</span>
        </div>
        <el-button type="primary" icon="el-icon-plus" @click="openSlot()">新增时段</el-button>
      </div>
      <el-table :data="slots" border empty-text="暂无集送时段">
        <el-table-column prop="deliveryTime" label="集送时间" width="130" align="center" />
        <el-table-column prop="workerName" label="集送人员" min-width="180" />
        <el-table-column prop="workerRole" label="身份" width="100" align="center" />
        <el-table-column prop="sort" label="排序" width="90" align="center" />
        <el-table-column label="状态" width="100" align="center">
          <template slot-scope="scope">
            <el-tag :type="scope.row.status === 1 ? 'success' : 'info'">{{ scope.row.status === 1 ? '启用' : '禁用' }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="150" align="center">
          <template slot-scope="scope">
            <el-button type="text" icon="el-icon-edit" @click="openSlot(scope.row)">编辑</el-button>
            <el-button type="text" class="danger-text" icon="el-icon-delete" @click="removeSlot(scope.row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <el-dialog :title="locationForm.id ? '编辑取件地点' : '新增取件地点'" :visible.sync="locationVisible" width="420px">
      <el-form :model="locationForm" label-width="80px">
        <el-form-item label="地点名称">
          <el-input v-model.trim="locationForm.locationName" maxlength="100" placeholder="例如：东区菜鸟驿站" />
        </el-form-item>
        <el-form-item label="排序">
          <el-input-number v-model="locationForm.sort" :min="0" />
        </el-form-item>
        <el-form-item label="状态">
          <el-switch v-model="locationForm.status" :active-value="1" :inactive-value="0" />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="locationVisible = false">取消</el-button>
        <el-button type="primary" @click="saveLocation">保存</el-button>
      </span>
    </el-dialog>

    <el-dialog :title="slotForm.id ? '编辑集送时段' : '新增集送时段'" :visible.sync="slotVisible" width="460px">
      <el-form :model="slotForm" label-width="90px">
        <el-form-item label="集送时间">
          <el-time-select v-model="slotForm.deliveryTime" :picker-options="{ start: '06:00', step: '00:10', end: '23:00' }" placeholder="选择时间" />
        </el-form-item>
        <el-form-item label="集送人员">
          <el-select v-model="slotForm.workerUserId" filterable placeholder="选择本校负责人或兼职" style="width: 100%">
            <el-option v-for="worker in workers" :key="worker.campusUserId" :label="worker.jobName + '（' + roleText(worker.jobType) + '）'" :value="worker.campusUserId" />
          </el-select>
        </el-form-item>
        <el-form-item label="排序">
          <el-input-number v-model="slotForm.sort" :min="0" />
        </el-form-item>
        <el-form-item label="状态">
          <el-switch v-model="slotForm.status" :active-value="1" :inactive-value="0" />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="slotVisible = false">取消</el-button>
        <el-button type="primary" @click="saveSlot">保存</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { isSchoolScoped, getBoundSchoolId, lockSchoolOptions } from '@/utils/schoolScope'
export default {
  computed: {
    schoolLocked() {
      return isSchoolScoped()
    }
  },
  data() {
    return {
      loading: false,
      schoolId: null,
      schools: [],
      config: this.defaultConfig(),
      locations: [],
      slots: [],
      workers: [],
      locationVisible: false,
      slotVisible: false,
      locationForm: this.defaultLocation(),
      slotForm: this.defaultSlot()
    }
  },
  created() {
    this.loadSchools()
  },
  methods: {
    defaultConfig() {
      return { schoolId: null, enabled: 0, cutoffMinutes: 10, smallPrice: 0, mediumPrice: 0, largePrice: 0 }
    },
    defaultLocation() {
      return { id: null, schoolId: null, locationName: '', sort: 0, status: 1, delFlag: '0' }
    },
    defaultSlot() {
      return { id: null, schoolId: null, deliveryTime: '', workerUserId: null, sort: 0, status: 1, delFlag: '0' }
    },
    roleText(type) {
      return type === 'F' ? '负责人' : '兼职'
    },
    async loadSchools() {
      const { data: res } = await this.$http.get('/school/getSchools')
      this.schools = lockSchoolOptions(res.data || [])
      if (isSchoolScoped() && getBoundSchoolId() != null) {
        this.schoolId = getBoundSchoolId()
        this.loadConfig()
        return
      }
      if (this.schools.length) {
        this.schoolId = this.schools[0].schoolId
        this.loadConfig()
      }
    },
    async loadConfig() {
      if (!this.schoolId) return
      this.loading = true
      try {
        const { data: res } = await this.$http.get('/admin/collectiveDelivery/config', { params: { schoolId: this.schoolId } })
        const data = res.data || {}
        this.config = Object.assign(this.defaultConfig(), data.config || {}, { schoolId: this.schoolId })
        this.locations = data.locations || []
        this.slots = data.slots || []
        this.workers = data.workers || []
      } finally {
        this.loading = false
      }
    },
    async saveConfig() {
      const { data: res } = await this.$http.post('/admin/collectiveDelivery/config', Object.assign({}, this.config, { schoolId: this.schoolId }))
      if (res.code === 200) {
        this.$message.success('集送配置已保存')
        this.loadConfig()
      } else this.$message.error(res.msg || '保存失败')
    },
    openLocation(row) {
      this.locationForm = Object.assign(this.defaultLocation(), row || {}, { schoolId: this.schoolId })
      this.locationVisible = true
    },
    async saveLocation() {
      if (!this.locationForm.locationName) return this.$message.warning('请输入取件地点')
      const { data: res } = await this.$http.post('/admin/collectiveDelivery/location', this.locationForm)
      if (res.code === 200) {
        this.locationVisible = false
        this.$message.success('取件地点已保存')
        this.loadConfig()
      } else this.$message.error(res.msg || '保存失败')
    },
    removeLocation(row) {
      this.$confirm(`确认删除“${row.locationName}”吗？`, '提示', { type: 'warning' }).then(async () => {
        await this.$http.delete('/admin/collectiveDelivery/location', { params: { id: row.id } })
        this.$message.success('已删除')
        this.loadConfig()
      }).catch(() => {})
    },
    openSlot(row) {
      this.slotForm = Object.assign(this.defaultSlot(), row || {}, { schoolId: this.schoolId })
      this.slotVisible = true
    },
    async saveSlot() {
      if (!this.slotForm.deliveryTime || !this.slotForm.workerUserId) return this.$message.warning('请选择集送时间和人员')
      const { data: res } = await this.$http.post('/admin/collectiveDelivery/slot', this.slotForm)
      if (res.code === 200) {
        this.slotVisible = false
        this.$message.success('集送时段已保存')
        this.loadConfig()
      } else this.$message.error(res.msg || '保存失败')
    },
    removeSlot(row) {
      this.$confirm(`确认删除 ${row.deliveryTime} 集送时段吗？`, '提示', { type: 'warning' }).then(async () => {
        await this.$http.delete('/admin/collectiveDelivery/slot', { params: { id: row.id } })
        this.$message.success('已删除')
        this.loadConfig()
      }).catch(() => {})
    }
  }
}
</script>

<style scoped>
.collective-page { padding: 4px 8px 32px; color: #1f2937; }
.page-header, .section-heading { display: flex; align-items: center; justify-content: space-between; gap: 20px; }
.page-header { margin-bottom: 20px; }
h2, h3 { margin: 0; font-weight: 600; }
h2 { font-size: 24px; }
h3 { font-size: 17px; }
p, .section-heading span { margin: 6px 0 0; color: #6b7280; font-size: 13px; }
.section-block { padding: 22px 0 28px; border-top: 1px solid #e5e7eb; }
.section-heading { margin-bottom: 18px; }
.config-form { display: grid; grid-template-columns: repeat(3, minmax(240px, 1fr)); column-gap: 24px; max-width: 1100px; }
.unit-text { margin-left: 8px; color: #6b7280; }
.danger-text { color: #f56c6c; }
@media (max-width: 1100px) { .config-form { grid-template-columns: repeat(2, minmax(240px, 1fr)); } }
</style>
